
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, View, Text, Pressable, Linking } from 'react-native';
import { useIsFocused } from '@react-navigation/native';

const ORIGIN = 'https://collabbay.com';
const API = ORIGIN + '/APIControllers/GetCreatorMap.php';
const TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const CREDIT = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const BATCH = 18;

let leafletLoad;

function loadFile(id, url, css = false) {
  return new Promise((resolve, reject) => {
    let element = document.getElementById(id);
    if (element?.dataset.loaded === 'yes' || (css && element?.sheet)) return resolve();
    if (element?.dataset.failed) {
      element.remove();
      element = null;
    }

    const fresh = !element;
    element = element || document.createElement(css ? 'link' : 'script');
    let timer;

    const clean = () => {
      clearTimeout(timer);
      element.removeEventListener('load', done);
      element.removeEventListener('error', fail);
    };
    const done = () => {
      element.dataset.loaded = 'yes';
      clean();
      resolve();
    };
    const fail = () => {
      element.dataset.failed = 'yes';
      clean();
      reject(new Error('Could not load map resources.'));
    };

    element.addEventListener('load', done);
    element.addEventListener('error', fail);
    timer = setTimeout(fail, 16000);

    if (fresh) {
      element.id = id;
      if (css) {
        element.rel = 'stylesheet';
        element.href = url;
      } else {
        element.async = true;
        element.src = url;
      }
      document.head.appendChild(element);
    }
  });
}

function getLeaflet() {
  if (!leafletLoad) {
    leafletLoad = (async () => {
      await loadFile(
        'cmx-leaflet-css',
        'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
        true
      );
      if (!window.L) {
        await loadFile(
          'cmx-leaflet-js',
          'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
        );
      }
      if (!window.L) throw new Error('The map library is unavailable.');

      try {
        await loadFile(
          'cmx-cluster-css',
          'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css',
          true
        );
        if (!window.L.markerClusterGroup) {
          await loadFile(
            'cmx-cluster-js',
            'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js'
          );
        }
      } catch (_) {
        // Ordinary pins still work without clustering.
      }
      return window.L;
    })().catch(error => {
      leafletLoad = null;
      throw error;
    });
  }
  return leafletLoad;
}

function safeUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return '';
  try {
    const url = new URL(value, ORIGIN);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return '';
    if (['collabbay.com', 'www.collabbay.com'].includes(url.hostname)) {
      url.protocol = 'https:';
    }
    return url.href;
  } catch (_) {
    return '';
  }
}

function gradeScore(value) {
  return { A: 1, AA: 2, AAA: 3 }[
    String(value || '').toUpperCase().trim()
  ] || 0;
}

function mediaType(m) {
  if (['image', 'video', 'link'].includes(m?.kind)) return m.kind;
  try {
    const u = new URL(m.url);
    if (/(^|\.)(instagram\.com|tiktok\.com|youtube\.com|youtu\.be|facebook\.com|vimeo\.com)$/.test(u.hostname)) {
      return 'link';
    }
    if (/\.(mp4|mov|m4v|webm|ogg|ogv)$/i.test(u.pathname)) return 'video';
    if (/\.(jpe?g|png|gif|avif|webp)$/i.test(u.pathname)) return 'image';
  } catch (_) {}
  return 'unknown';
}

function normalize(payload) {
  if (!payload?.ok || !Array.isArray(payload.venues)) {
    throw new Error(payload?.message || 'Unexpected map response');
  }

  return payload.venues.map(v => ({
    ...v,
    id: String(v.id),
    name: String(v.name || 'Venue'),
    lat: Number(v.lat),
    lng: Number(v.lng),
    area: String(v.area || ''),
    address: String(v.address || ''),
    city: String(v.city || ''),
    booking_url: safeUrl(v.booking_url),
    media: (Array.isArray(v.media) ? v.media : [])
      .map((m, index) => ({
        ...m,
        id: `${v.id}-${index}`,
        creator: String(m.creator || ''),
        url: safeUrl(m.url),
        original_url: safeUrl(m.original_url || m.url),
        poster: safeUrl(m.poster),
        creator_rating: String(m.creator_rating || ''),
        rank: gradeScore(m.creator_rating),
      }))
      .filter(m => m.url && m.creator),
  }))
    .filter(v =>
      Number.isFinite(v.lat) &&
      Number.isFinite(v.lng) &&
      Math.abs(v.lat) <= 85.0511 &&
      Math.abs(v.lng) <= 180 &&
      v.media.length
    )
    .map(v => ({
      ...v,
      featured: Math.max(0, ...v.media.map(m => m.rank)),
    }));
}

function kmBetween(a, b) {
  const r = x => x * Math.PI / 180;
  const dLat = r(b.lat - a.lat);
  const dLon = r(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(r(a.lat)) * Math.cos(r(b.lat)) *
    Math.sin(dLon / 2) ** 2;
  return 6371.0088 * 2 * Math.atan2(
    Math.sqrt(Math.min(1, h)),
    Math.sqrt(Math.max(0, 1 - h))
  );
}

function distanceText(km) {
  return km < 1
    ? `${Math.round(km * 1000)} m away`
    : `${km.toFixed(1)} km away`;
}

function directionsUrl(v) {
  const text = [
    v.name, v.address, v.area, v.city || 'London',
  ].filter(Boolean).join(', ');
  return 'https://www.google.com/maps/search/?api=1&query=' +
    encodeURIComponent(text);
}

function profileUrl(name) {
  return ORIGIN + '/creatormap' +
    (name ? '/' + encodeURIComponent(name) : '');
}

function latest(m) {
  return Date.parse(m.uploaded_at || '') || 0;
}
function shortCreator(name) {
  return String(name || '').replace(/^@/, '').slice(0, 12);
}
function jump(url) {
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

// Media loads video only when visible, muted by default.
function Media({
  item,
  active = true,
  poster = '',
  className = '',
  controls = false,
}) {
  const ref = useRef(null);
  const player = useRef(null);
  const [visible, setVisible] = useState(false);
  const [sound, setSound] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const type = mediaType(item);
  const effective = type === 'unknown'
    ? (attempt === 0 ? 'image' : 'video')
    : type;

  useEffect(() => {
    setError(false);
    setSound(false);
    setAttempt(0);
  }, [item?.url]);

  useEffect(() => {
    if (!ref.current) return;
    if (!window.IntersectionObserver) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(
        entry.isIntersecting && entry.intersectionRatio >= .35
      ),
      { threshold: [.05, .35, .7] }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = player.current;
    if (!el) return;

    if (active && visible && !document.hidden) {
      el.play()?.catch(() => {});
    } else {
      el.pause();
    }
    return () => el.pause();
  }, [active, visible, item?.url, effective, error]);

  function toggleSound(event) {
    event.stopPropagation();
    const el = player.current;
    if (!el) return;

    const next = !sound;
    document.querySelectorAll('video[data-cmx-video]').forEach(other => {
      if (other !== el) {
        other.muted = true;
        other.pause();
      }
    });
    el.muted = !next;
    setSound(next);
    el.play()?.catch(() => {});
  }

  return (
    <div ref={ref} className={'cmx-media ' + className}>
      {!item?.url || error || type === 'link' ? (
        <div className="cmx-fallback">
          <span>▶</span>
          <p>{type === 'link' ? 'See the original post' : 'Preview unavailable'}</p>
          <button type="button" onClick={() => jump(item?.original_url || item?.url)}>
            Open content ↗
          </button>
        </div>
      ) : effective === 'image' ? (
        <img
          src={item.url}
          alt="Creator visit"
          loading="lazy"
          onError={() => {
            if (type === 'unknown') setAttempt(1);
            else setError(true);
          }}
        />
      ) : (
        <>
          <video
            ref={player}
            data-cmx-video="yes"
            src={visible ? item.url : undefined}
            poster={item.poster || poster || undefined}
            muted
            playsInline
            loop
            preload="metadata"
            controls={controls}
            onError={() => setError(true)}
          />
          <button
            className="cmx-sound"
            type="button"
            onClick={toggleSound}
            aria-label={sound ? 'Mute video' : 'Turn on sound'}
          >
            {sound ? '♪ On' : '♪ Sound'}
          </button>
        </>
      )}
    </div>
  );
}

function Detail({ venue, initial = 0, onClose, onCreator }) {
  const [index, setIndex] = useState(initial);
  const selected = venue.media[Math.min(index, venue.media.length - 1)];

  useEffect(() => {
    function key(event) {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') {
        setIndex(i => (i + 1) % venue.media.length);
      }
      if (event.key === 'ArrowLeft') {
        setIndex(i => (i - 1 + venue.media.length) % venue.media.length);
      }
    }
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose, venue]);

  return (
    <div
      className="cmx-detail-shade"
      onMouseDown={e => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <section
        className="cmx-detail"
        role="dialog"
        aria-modal="true"
        aria-label={venue.name}
      >
        <div className="cmx-detail-image">
          <Media
            key={selected.id}
            item={selected}
            active
            controls
            poster={selected.poster}
          />
        </div>

        <div className="cmx-detail-info">
          <button
            className="cmx-detail-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >×</button>

          <div className="cmx-eyebrow">{venue.area || 'Discover'}</div>
          <h2>{venue.name}</h2>

          <p className="cmx-detail-author">
            By <button type="button" onClick={() => onCreator(selected.creator)}>
              @{selected.creator}
            </button>
            {selected.rank > 0 ? ' · Featured creator' : ''}
          </p>

          <div className="cmx-detail-pages">
            <button
              type="button"
              onClick={() => setIndex(
                i => (i - 1 + venue.media.length) % venue.media.length
              )}
              disabled={venue.media.length === 1}
            >‹</button>
            <span>{index + 1} of {venue.media.length}</span>
            <button
              type="button"
              onClick={() => setIndex(i => (i + 1) % venue.media.length)}
              disabled={venue.media.length === 1}
            >›</button>
          </div>

          <div className="cmx-detail-thumbs">
            {venue.media.slice(0, 12).map((m, i) => (
              <button
                type="button"
                key={m.id}
                className={i === index ? 'selected' : ''}
                onClick={() => setIndex(i)}
                aria-label={'Post ' + (i + 1)}
              >
                {m.poster ? <img src={m.poster} alt="" /> :
                  mediaType(m) === 'image' ? <img src={m.url} alt="" /> : '▶'}
              </button>
            ))}
          </div>

          <div className="cmx-detail-buttons">
            {venue.booking_url && (
              <a
                className="cmx-cta"
                target="_blank"
                rel="noopener noreferrer"
                href={venue.booking_url}
              >Book this place ↗</a>
            )}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={directionsUrl(venue)}
            >Find address ↗</a>
          </div>

          {selected.original_url && (
            <a
              className="cmx-original"
              href={selected.original_url}
              target="_blank"
              rel="noopener noreferrer"
            >View original post ↗</a>
          )}
        </div>
      </section>
    </div>
  );
}

function CreatorMapWeb({ route, navigation }) {
  const focused = useIsFocused();
  const creator = typeof route?.params?.Promoter === 'string'
    ? route.params.Promoter.trim()
    : '';

  const [view, setView] = useState('map');
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [area, setArea] = useState('');
  const [location, setLocation] = useState(null);
  const [radius, setRadius] = useState(10);
  const [finding, setFinding] = useState(false);
  const [selected, setSelected] = useState(null);
  const [peek, setPeek] = useState(null);
  const [visibleCards, setVisibleCards] = useState(BATCH);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');

  const host = useRef(null);
  const mapRef = useRef(null);
  const hoverTime = useRef(null);
  const awayTime = useRef(null);
  const feeds = useRef(null);
  const sentinel = useRef(null);
  const alive = useRef(true);
  const mapNeedsFit = useRef(true);
  const live = useRef({});

  const chooseCreator = useCallback(name => {
    setSelected(null);
    setPeek(null);
    setArea('');
    setQuery('');
    navigation.navigate('CreatorMap', {
      Promoter: name || undefined,
    });
  }, [navigation]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(hoverTime.current);
      clearTimeout(awayTime.current);
    };
  }, []);

  useEffect(() => {
    if (!focused) return;
    const original = document.title;
    document.title = creator
      ? `${creator}'s food map | collabbay`
      : 'Find food near you | collabbay';
    return () => { document.title = original; };
  }, [creator, focused]);

  useEffect(() => {
    const abort = new AbortController();
    let active = true;

    setLoading(true);
    setError('');
    setSelected(null);
    setPeek(null);

    const url = new URL(API);
    if (creator) url.searchParams.set('creator', creator);

    const timer = setTimeout(() => abort.abort(), 30000);

    fetch(url.href, {
      signal: abort.signal,
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })
      .then(async response => {
        const body = await response.json();
        if (!response.ok || !body.ok) {
          throw new Error(body.message || 'Could not load the map');
        }
        return normalize(body);
      })
      .then(rows => {
        if (active) {
          setAll(rows);
          mapNeedsFit.current = true;
        }
      })
      .catch(err => {
        if (active) {
          setError(err.name === 'AbortError'
            ? 'Request timed out. Try again.'
            : err.message);
        }
      })
      .finally(() => {
        clearTimeout(timer);
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      clearTimeout(timer);
      abort.abort();
    };
  }, [creator]);

  const areas = useMemo(
    () => [...new Set(all.map(v => v.area).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)),
    [all]
  );

  const venues = useMemo(() => {
    const text = query.trim().toLowerCase();

    return all
      .map(v => ({
        ...v,
        distance: location ? kmBetween(location, v) : null,
      }))
      .filter(v =>
        (!area || v.area === area) &&
        (!text || [
          v.name, v.area, v.address,
          ...v.media.map(m => m.creator),
        ].join(' ').toLowerCase().includes(text)) &&
        (!location || radius === 0 || v.distance <= radius)
      )
      .sort((a, b) =>
        location
          ? a.distance - b.distance
          : b.featured - a.featured ||
            (Date.parse(b.latest_at || '') || 0) -
            (Date.parse(a.latest_at || '') || 0)
      );
  }, [all, query, location, radius, area]);

  const posts = useMemo(
    () => venues.flatMap(venue =>
      venue.media.map(media => ({
        venue,
        media,
        distance: venue.distance,
      }))
    ).sort((a, b) =>
      location
        ? a.distance - b.distance || b.media.rank - a.media.rank
        : b.media.rank - a.media.rank ||
          latest(b.media) - latest(a.media)
    ),
    [venues, location]
  );

  const spotlight = useMemo(() => {
    const candidates = posts.slice().sort(
      (a, b) => b.media.rank - a.media.rank ||
        latest(b.media) - latest(a.media)
    );
    const picked = [];
    const used = new Set();

    candidates.forEach(post => {
      if (picked.length < 3 && !used.has(post.venue.id)) {
        picked.push(post);
        used.add(post.venue.id);
      }
    });
    return picked;
  }, [posts]);

  live.current = { venues, location, view, focused, loading };

  useEffect(() => {
    mapNeedsFit.current = true;
  }, [venues]);

  useEffect(() => {
    setVisibleCards(BATCH);
    if (feeds.current) feeds.current.scrollTop = 0;
  }, [posts.length, creator, area, query, location, radius]);

  const dismissPeek = useCallback(() => {
    clearTimeout(hoverTime.current);
    clearTimeout(awayTime.current);
    setPeek(null);
  }, []);

  const openVenue = useCallback((venue, index = 0) => {
    dismissPeek();
    setSelected({ venue, index });
  }, [dismissPeek]);

  const fit = useCallback(() => {
    if (!mapRef.current || live.current.view !== 'map') {
      mapNeedsFit.current = true;
      return;
    }

    const points = live.current.venues.map(v => [v.lat, v.lng]);

    if (live.current.location) {
      points.push([
        live.current.location.lat,
        live.current.location.lng,
      ]);
    }

    if (points.length === 1) {
      mapRef.current.map.setView(points[0], 15);
    } else if (points.length > 1) {
      mapRef.current.map.fitBounds(points, {
        paddingTopLeft: [45, 135],
        paddingBottomRight: [45, 210],
        maxZoom: 15,
      });
    }

    mapNeedsFit.current = false;
  }, []);

  useEffect(() => {
    let stopped = false;
    let observer;
    let frame;
    let state;

    getLeaflet().then(L => {
      if (stopped || !host.current) return;

      const map = L.map(host.current, {
        zoomControl: false,
        preferCanvas: true,
      }).setView([51.5074, -0.1278], 12);

      L.tileLayer(TILES, {
        attribution: CREDIT,
        maxZoom: 19,
      }).addTo(map);

      L.control.zoom({ position: 'bottomright' }).addTo(map);

      const group = L.markerClusterGroup
        ? L.markerClusterGroup({
            maxClusterRadius: 46,
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: true,
            iconCreateFunction: cluster => L.divIcon({
              className: 'cmx-cluster',
              html: String(cluster.getChildCount()),
              iconSize: [48, 48],
            }),
          })
        : L.featureGroup();

      group.addTo(map);

      state = {
        L, map, group,
        markers: new Map(),
        locationLayer: L.layerGroup().addTo(map),
      };

      mapRef.current = state;

      map.on('zoomend', () => {
        host.current?.classList.toggle(
          'cmx-closezoom',
          map.getZoom() >= 13
        );
      });
      map.on('movestart', dismissPeek);
      map.on('click', dismissPeek);

      const resize = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => map.invalidateSize());
      };
      window.addEventListener('resize', resize);

      if (window.ResizeObserver) {
        observer = new ResizeObserver(resize);
        observer.observe(host.current);
      }

      state.cleanup = () => window.removeEventListener('resize', resize);
      setMapReady(true);
    }).catch(err => {
      if (!stopped) setMapError(err.message);
    });

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();

      if (state) {
        state.cleanup?.();
        state.map.remove();
        if (mapRef.current === state) mapRef.current = null;
      }
    };
  }, [dismissPeek]);

  useEffect(() => {
    const state = mapRef.current;
    if (!state || !mapReady) return;

    const { L, group, map, markers } = state;
    group.clearLayers();
    markers.clear();

    venues.forEach(v => {
      const face = document.createElement('div');
      face.className = 'cmx-face' + (v.featured ? ' featured' : '');

      const center = document.createElement('button');
      center.type = 'button';
      center.className = 'cmx-core';
      center.setAttribute('aria-label', `Open ${v.name}`);

      const photo = v.media.find(
        m => m.poster || mediaType(m) === 'image'
      );

      if (photo) {
        const img = document.createElement('img');
        img.src = photo.poster || photo.url;
        img.alt = '';
        img.onerror = () => {
          img.remove();
          center.textContent = v.name.slice(0, 1);
        };
        center.appendChild(img);
      } else {
        center.textContent = v.name.slice(0, 1);
      }

      face.appendChild(center);

      if (v.media.length > 1) {
        const count = document.createElement('span');
        count.className = 'cmx-count';
        count.textContent = v.media.length;
        face.appendChild(count);

        v.media.slice(0, 6).forEach((m, index) => {
          const satellite = document.createElement('button');
          satellite.type = 'button';
          satellite.className = 'cmx-sat cmx-sat-' + index;
          satellite.setAttribute(
            'aria-label',
            `Preview ${v.name} post ${index + 1}`
          );
          satellite.textContent =
            mediaType(m) === 'video' ? '▶' : '•';

          const imageUrl =
            m.poster ||
            (mediaType(m) === 'image' ? m.url : '');

          if (imageUrl) {
            satellite.textContent = '';
            const img = document.createElement('img');
            img.src = imageUrl;
            img.alt = '';
            satellite.appendChild(img);
          }

          satellite.addEventListener('mouseenter', () => {
            preview(v, m, index);
          });

          satellite.addEventListener('click', e => {
            L.DomEvent.stop(e);
            openVenue(v, index);
          });

          face.appendChild(satellite);
        });
      }

      const marker = L.marker([v.lat, v.lng], {
        title: v.name,
        riseOnHover: true,
        icon: L.divIcon({
          html: face,
          className: 'cmx-pin',
          iconSize: [52, 52],
          iconAnchor: [26, 26],
        }),
      });

      function preview(venue, media, index) {
        if (
          !window.matchMedia('(hover:hover)').matches ||
          !live.current.focused
        ) return;

        clearTimeout(awayTime.current);
        clearTimeout(hoverTime.current);

        hoverTime.current = setTimeout(() => {
          const p = map.latLngToContainerPoint(marker.getLatLng());
          const size = map.getSize();

          setPeek({
            venue,
            media,
            index,
            x: Math.max(
              12, Math.min(size.x - 228, p.x + 25)
            ),
            y: Math.max(
              135, Math.min(size.y - 270, p.y - 140)
            ),
          });
        }, 150);
      }

      marker.on('mouseover', () => preview(v, v.media[0], 0));
      marker.on('mouseout', () => {
        clearTimeout(hoverTime.current);
        awayTime.current = setTimeout(() => setPeek(null), 280);
      });
      marker.on('click', () => openVenue(v));

      group.addLayer(marker);
      markers.set(v.id, marker);
    });

    if (!loading) fit();
  }, [mapReady, venues, loading, fit, openVenue]);

  useEffect(() => {
    const state = mapRef.current;
    if (!state || !mapReady) return;

    state.locationLayer.clearLayers();

    if (location) {
      state.L.circleMarker(
        [location.lat, location.lng],
        {
          radius: 8,
          color: '#fff',
          weight: 3,
          fillColor: '#3577ef',
          fillOpacity: 1,
        }
      )
        .addTo(state.locationLayer)
        .bindTooltip('You are here');
    }
  }, [mapReady, location]);

  useEffect(() => {
    dismissPeek();
    if (view !== 'map') return;

    const frame = requestAnimationFrame(() => {
      mapRef.current?.map.invalidateSize();
      if (mapNeedsFit.current) fit();
    });

    return () => cancelAnimationFrame(frame);
  }, [view, focused, mapReady, fit, dismissPeek]);

  useEffect(() => {
    if (
      !sentinel.current ||
      !window.IntersectionObserver ||
      view !== 'feed' ||
      visibleCards >= posts.length
    ) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCards(n => Math.min(n + BATCH, posts.length));
        }
      },
      {
        root: feeds.current,
        rootMargin: '300px',
      }
    );

    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [view, visibleCards, posts.length]);

  const nearMe = () => {
    if (location) {
      setLocation(null);
      return;
    }

    if (!navigator.geolocation || !window.isSecureContext) {
      setError('Near me requires HTTPS and location permission.');
      return;
    }

    setFinding(true);

    navigator.geolocation.getCurrentPosition(
      position => {
        if (alive.current) {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setFinding(false);
          mapNeedsFit.current = true;
        }
      },
      () => {
        if (alive.current) {
          setFinding(false);
          setError('Location unavailable. Search by neighbourhood instead.');
        }
      },
      {
        timeout: 12000,
        maximumAge: 60000,
        enableHighAccuracy: false,
      }
    );
  };

  return (
    <div className={'cmx-shell cmx-' + view}>
      <style>{CSS}</style>

      {/* Map */}
      <div
        className="cmx-map-section"
        style={{ display: view === 'map' ? 'block' : 'none' }}
      >
        <div ref={host} className="cmx-map" />

        {mapError && (
          <div className="cmx-map-error">{mapError}</div>
        )}

        {peek && !selected && focused && (
          <div
            className="cmx-peek"
            style={{ left: peek.x, top: peek.y }}
            onMouseEnter={() => clearTimeout(awayTime.current)}
            onMouseLeave={() => setPeek(null)}
          >
            <Media
              item={peek.media}
              active
              poster={peek.media.poster}
            />
            <button
              type="button"
              onClick={() => openVenue(peek.venue, peek.index)}
            >
              <strong>{peek.venue.name}</strong>
              <span>Explore ↗</span>
            </button>
          </div>
        )}

        {/* Preloaded editorial spotlights */}
        <div className="cmx-map-rail" aria-label="Featured places">
          {spotlight.map(({ venue, media }, i) => (
            <div
              role="button"
              tabIndex={0}
              className={'cmx-spot cmx-spot-' + i}
              key={media.id}
              onClick={() => openVenue(
                venue,
                venue.media.indexOf(media)
              )}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openVenue(venue, venue.media.indexOf(media));
                }
              }}
            >
              <Media
                item={media}
                active={
                  focused &&
                  view === 'map' &&
                  i === 0 &&
                  !selected
                }
                poster={media.poster}
              />
              <div className="cmx-spot-overlay">
                <strong>{venue.name}</strong>
                <span>
                  {venue.area}
                  {venue.media.length > 1
                    ? ` · ${venue.media.length} posts`
                    : ''}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="cmx-map-count">
          {loading
            ? 'Finding places…'
            : `${venues.length} places · ${posts.length} posts`}
        </div>
      </div>

      {/* Feed */}
      <div
        className="cmx-feed"
        ref={feeds}
        style={{ display: view === 'feed' ? 'block' : 'none' }}
      >
        <div className="cmx-feed-intro">
          <span className="cmx-eyebrow">EXPLORE WHAT'S GOOD</span>
          <h1>
            {location
              ? 'Around you.'
              : creator
                ? `${creator}'s places.`
                : 'Worth a visit.'}
          </h1>
          <p>
            {posts.length} creator posts in {venues.length} places
          </p>
        </div>

        <div className="cmx-post-grid">
          {posts.slice(0, visibleCards).map(
            ({ venue, media, distance }) => (
              <article
                className={
                  'cmx-post' +
                  (media.rank ? ' cmx-post-featured' : '')
                }
                key={media.id}
              >
                <div className="cmx-post-picture">
                  <Media
                    item={media}
                    active={
                      focused &&
                      view === 'feed' &&
                      !selected
                    }
                    poster={media.poster}
                  />

                  <button
                    type="button"
                    className="cmx-open-post"
                    aria-label={`View ${venue.name}`}
                    onClick={() => openVenue(
                      venue,
                      venue.media.indexOf(media)
                    )}
                  />
                </div>

                <div className="cmx-post-copy">
                  <div className="cmx-post-top">
                    <span>
                      {venue.area || 'London'}
                      {distance !== null
                        ? ` · ${distanceText(distance)}`
                        : ''}
                    </span>

                    {media.rank > 0 && (
                      <small>✦ CREATOR PICK</small>
                    )}
                  </div>

                  <h2>{venue.name}</h2>

                  <button
                    className="cmx-author"
                    type="button"
                    onClick={() => chooseCreator(media.creator)}
                  >
                    by @{shortCreator(media.creator)}
                  </button>

                  <div className="cmx-post-links">
                    {venue.booking_url && (
                      <a
                        href={venue.booking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cmx-book"
                      >
                        Book ↗
                      </a>
                    )}

                    <a
                      href={directionsUrl(venue)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Find address ↗
                    </a>

                    <button
                      type="button"
                      onClick={() => openVenue(
                        venue,
                        venue.media.indexOf(media)
                      )}
                    >
                      More posts · {venue.media.length}
                    </button>
                  </div>
                </div>
              </article>
            )
          )}
        </div>

        {posts.length > visibleCards && (
          <div ref={sentinel} className="cmx-load">
            <button
              type="button"
              onClick={() => setVisibleCards(n => n + BATCH)}
            >
              More posts
            </button>
          </div>
        )}
      </div>

      {/* Controls */}
      <header className="cmx-header">
        <a href={ORIGIN} className="cmx-logo">
          collabbay<sup>®</sup>
        </a>

        <div className="cmx-search">
          <span>⌕</span>
          <input
            type="search"
            placeholder="Restaurant, area, creator…"
            aria-label="Search restaurants"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={'cmx-near' + (location ? ' on' : '')}
          onClick={nearMe}
        >
          {finding
            ? 'Locating…'
            : location
              ? '◎ Near me on'
              : '◎ Near me'}
        </button>
      </header>

      <div className="cmx-area-bar">
        <div className="cmx-chips">
          <button
            type="button"
            className={!area ? 'selected' : ''}
            onClick={() => setArea('')}
          >
            Everywhere
          </button>

          {areas.map(name => (
            <button
              type="button"
              key={name}
              className={area === name ? 'selected' : ''}
              onClick={() => setArea(v => v === name ? '' : name)}
            >
              {name}
            </button>
          ))}
        </div>

        {location && (
          <select
            aria-label="Nearby radius"
            value={radius}
            onChange={e => setRadius(Number(e.target.value))}
          >
            {[2, 5, 10, 25, 50, 0].map(n => (
              <option key={n} value={n}>
                {n ? `${n} km` : 'All distances'}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Floating Map / Feed switch */}
      <nav className="cmx-dock" aria-label="View switcher">
        <span
          className={
            'cmx-dock-indicator' +
            (view === 'feed' ? ' right' : '')
          }
        />
        <button
          type="button"
          aria-pressed={view === 'map'}
          onClick={() => setView('map')}
        >
          ⌖ <span>Map</span>
        </button>
        <button
          type="button"
          aria-pressed={view === 'feed'}
          onClick={() => setView('feed')}
        >
          ▦ <span>Feed</span>
        </button>
      </nav>

      {error && (
        <div className="cmx-error" role="alert">
          {error}
          <button type="button" onClick={() => setError('')}>×</button>
        </div>
      )}

      {!loading && !venues.length && !error && (
        <div className="cmx-empty">
          <h2>No places found</h2>
          <p>Try a different area or widen your radius.</p>
          <button
            type="button"
            onClick={() => {
              setArea('');
              setQuery('');
              setLocation(null);
            }}
          >
            Clear filters
          </button>
        </div>
      )}

      {selected && (
        <Detail
          key={selected.venue.id + '-' + selected.index}
          venue={selected.venue}
          initial={selected.index}
          onClose={() => setSelected(null)}
          onCreator={chooseCreator}
        />
      )}
    </div>
  );
}

export default function CreatorMap(props) {
  if (Platform.OS !== 'web') {
    const name =
      typeof props.route?.params?.Promoter === 'string'
        ? props.route.params.Promoter
        : '';

    return (
      <View style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f6f7f1',
        padding: 24,
      }}>
        <Text style={{ marginBottom: 20, fontSize: 22 }}>
          Find your next restaurant
        </Text>
        <Pressable
          onPress={() => Linking.openURL(profileUrl(name))}
          style={{
            padding: 16,
            backgroundColor: '#fdf05d',
            borderRadius: 14,
          }}
        >
          <Text>Open creator map</Text>
        </Pressable>
      </View>
    );
  }

  return <CreatorMapWeb {...props} />;
}

const CSS = `
.cmx-shell{--gold:#fdf05d;--ink:#17201b;--muted:#6e7c70;position:relative;width:100%;height:100vh;height:100dvh;overflow:hidden;background:#edf0e8;color:var(--ink);font:14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;isolation:isolate}
.cmx-shell *{box-sizing:border-box}.cmx-shell button,.cmx-shell input,.cmx-shell select{font:inherit}.cmx-shell button{border:0;cursor:pointer}.cmx-shell a{text-decoration:none;color:inherit}.cmx-shell button:focus-visible,.cmx-shell a:focus-visible,.cmx-shell input:focus-visible{outline:3px solid #a6ae47;outline-offset:2px}
.cmx-map-section,.cmx-map{position:absolute;inset:0}.cmx-map{background:#e6ebe2}.cmx-map .leaflet-tile-pane{filter:grayscale(.75) saturate(.55) brightness(1.04)}.cmx-map .leaflet-control-attribution{font-size:9px;background:#ffffffcd}.cmx-map .leaflet-control-zoom{margin:0 18px 19px 0;border:0;box-shadow:0 7px 22px #22341c22}.cmx-map .leaflet-control-zoom a{color:#1a2a19;background:#ffffffed}
.cmx-header{position:absolute;top:19px;left:22px;right:22px;display:flex;align-items:center;gap:12px;z-index:700;pointer-events:none}.cmx-header>*{pointer-events:auto}.cmx-logo{border:1px solid #ffffff9e;background:#ffffffdf;backdrop-filter:blur(18px);padding:12px 16px;border-radius:16px;font-size:24px;font-weight:550;letter-spacing:-1px;box-shadow:0 9px 26px #19231413;white-space:nowrap}.cmx-logo sup{font-size:9px;vertical-align:top}.cmx-search{display:flex;align-items:center;gap:9px;flex:1;max-width:540px;background:#ffffffdf;border:1px solid #ffffffda;border-radius:16px;backdrop-filter:blur(18px);box-shadow:0 9px 26px #19231413;padding:0 16px;height:50px}.cmx-search span{font-size:27px;color:#657767}.cmx-search input{width:100%;min-width:0;border:0;outline:0;background:none;font-size:13px}.cmx-near{margin-left:auto;background:#ffffffdf;border:1px solid #ffffffdb;border-radius:15px;backdrop-filter:blur(18px);box-shadow:0 9px 26px #19231413;padding:15px 17px;font-weight:650;white-space:nowrap}.cmx-near.on{background:#fdf05de8}
.cmx-area-bar{position:absolute;top:83px;left:22px;right:22px;display:flex;align-items:center;gap:8px;z-index:650;pointer-events:none}.cmx-chips{display:flex;gap:7px;overflow-x:auto;scrollbar-width:none;flex:1;pointer-events:auto}.cmx-chips button{background:#ffffffe6;white-space:nowrap;border:1px solid #fff;border-radius:999px;padding:8px 13px;color:#516252;box-shadow:0 4px 12px #21351b0e;font-size:11px}.cmx-chips button.selected{background:#18251b;color:var(--gold)}.cmx-area-bar select{background:#fff;border:1px solid #fff;border-radius:12px;padding:7px 9px;pointer-events:auto;max-width:130px}
.cmx-pin{background:none;border:0}.cmx-face{position:relative;width:52px;height:52px}.cmx-core{width:48px;height:48px;border-radius:18px;border:3px solid white;background:#1e2a20;color:var(--gold);font-size:20px;font-weight:700;box-shadow:0 5px 17px #14241755;overflow:hidden;display:grid;place-items:center;position:absolute;top:2px;left:2px}.cmx-face.featured .cmx-core{border-color:var(--gold);box-shadow:0 0 0 3px #fdf05d44,0 6px 17px #14241755}.cmx-core img{height:100%;width:100%;object-fit:cover}.cmx-count{position:absolute;z-index:4;right:-8px;top:-8px;border:2px solid white;border-radius:20px;background:#1c2a1c;color:var(--gold);font-size:10px;min-width:22px;text-align:center;padding:2px 4px;pointer-events:none}.cmx-sat{display:none;position:absolute;width:25px;height:25px;background:var(--gold);border:2px solid white;color:#1c2a21;border-radius:50%;font-size:9px;box-shadow:0 4px 12px #122b2433;align-items:center;justify-content:center;overflow:hidden}.cmx-sat img{width:100%;height:100%;object-fit:cover}.cmx-closezoom .cmx-sat,.cmx-pin:hover .cmx-sat{display:flex}.cmx-sat-0{left:-23px;top:3px}.cmx-sat-1{left:-23px;top:33px}.cmx-sat-2{left:12px;top:-28px}.cmx-sat-3{left:49px;top:1px}.cmx-sat-4{left:49px;top:33px}.cmx-sat-5{left:12px;top:55px}.cmx-cluster{display:grid;place-items:center;border-radius:50%;background:#1b2c1e;border:5px solid var(--gold);color:var(--gold);font-weight:700;box-shadow:0 5px 14px #1a2a1a22}
.cmx-map-count{position:absolute;top:136px;left:22px;background:#ffffffdc;border:1px solid #fff;border-radius:14px;padding:9px 12px;font-size:11px;z-index:450;box-shadow:0 4px 12px #21351b0e}.cmx-map-rail{position:absolute;bottom:106px;left:22px;display:flex;align-items:end;gap:11px;z-index:440;max-width:calc(100% - 70px);pointer-events:none}.cmx-spot{pointer-events:auto;position:relative;width:155px;height:176px;overflow:hidden;border:4px solid white;border-radius:20px;background:#233326;box-shadow:0 12px 32px #14211855;padding:0;text-align:left;color:white}.cmx-spot-0{width:180px;height:236px}.cmx-spot-2{width:134px;height:150px}.cmx-spot .cmx-media{height:100%;width:100%;aspect-ratio:auto}.cmx-spot-overlay{position:absolute;bottom:0;left:0;right:0;padding:27px 12px 12px;background:linear-gradient(transparent,#000c);text-shadow:0 1px 4px #0008}.cmx-spot-overlay strong{font-size:13px;display:block}.cmx-spot-overlay span{font-size:10px;opacity:.85}.cmx-spot .cmx-sound{top:8px;right:8px;font-size:9px}
.cmx-peek{position:absolute;z-index:600;width:216px;border:4px solid white;border-radius:18px;overflow:hidden;box-shadow:0 10px 36px #1b28273b;background:#fff}.cmx-peek .cmx-media{height:170px;aspect-ratio:auto}.cmx-peek>button{padding:10px 12px;background:white;width:100%;display:flex;justify-content:space-between;gap:8px}.cmx-peek>button strong{font-size:12px}.cmx-peek>button span{font-size:11px}.cmx-map-error{position:absolute;top:146px;right:20px;z-index:500;background:#fff;padding:12px;border-radius:12px}
.cmx-media{position:relative;width:100%;aspect-ratio:4/5;background:linear-gradient(135deg,#25362a,#667965);overflow:hidden;display:flex;align-items:center;justify-content:center}.cmx-media img,.cmx-media video{height:100%;width:100%;object-fit:cover;display:block}.cmx-media video{background:#1c2521}.cmx-sound{position:absolute;right:12px;top:12px;z-index:4;padding:7px 9px;background:#111a16b5;color:white;border:1px solid #ffffff52;border-radius:20px;font-size:10px}.cmx-fallback{text-align:center;color:white;display:grid;gap:12px;padding:20px}.cmx-fallback span{font-size:32px}.cmx-fallback button{background:#fff;color:#121b15;border-radius:12px;padding:8px}
.cmx-feed{position:absolute;inset:0;overflow-y:auto;overscroll-behavior:contain;padding:148px 24px 125px;background:#f7f8f3}.cmx-feed-intro{max-width:1340px;margin:0 auto 28px}.cmx-eyebrow{font-size:10px;letter-spacing:2px;color:#788777;font-weight:750}.cmx-feed-intro h1{font-weight:700;font-size:clamp(28px,3.5vw,50px);line-height:1.06;letter-spacing:-1.6px;margin:6px 0}.cmx-feed-intro p{font-size:12px;color:var(--muted)}.cmx-post-grid{max-width:1340px;margin:auto;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:17px;align-items:start}.cmx-post{min-width:0;background:white;border:1px solid #e4e9e0;border-radius:22px;overflow:hidden;box-shadow:0 7px 25px #263c1e0b}.cmx-post-featured{border-color:#e9e39c}.cmx-post-picture{position:relative}.cmx-post-picture .cmx-media{aspect-ratio:4/5}.cmx-open-post{position:absolute;inset:0;background:transparent;z-index:1}.cmx-post-copy{padding:15px}.cmx-post-top{display:flex;justify-content:space-between;gap:7px;font-size:10px;color:#75836f}.cmx-post-top small{font-size:9px;color:#796c15;white-space:nowrap}.cmx-post h2{margin:7px 0 8px;font-size:25px;line-height:1.1;letter-spacing:-.8px}.cmx-author{padding:0;background:transparent;color:#536152;font-size:11px;text-align:left}.cmx-post-links{display:flex;align-items:center;flex-wrap:wrap;gap:10px;margin-top:15px}.cmx-post-links a,.cmx-post-links button{font-size:10px;background:transparent;color:#617062;padding:7px 1px}.cmx-post-links a.cmx-book{background:var(--gold);color:#1b281c;border-radius:10px;font-weight:750;font-size:12px;padding:10px 17px}.cmx-load{text-align:center;margin:26px}.cmx-load button{background:#1c2a1f;color:white;padding:13px 24px;border-radius:20px}
.cmx-dock{position:absolute;z-index:900;bottom:max(22px,env(safe-area-inset-bottom));left:50%;transform:translateX(-50%);display:grid;grid-template-columns:110px 110px;padding:5px;border-radius:22px;border:1px solid #fff;background:#ffffffc7;backdrop-filter:blur(22px) saturate(160%);box-shadow:0 12px 38px #192c1c33}.cmx-dock-indicator{position:absolute;left:5px;top:5px;bottom:5px;width:110px;border-radius:17px;background:#1c2b1f;transition:transform .2s}.cmx-dock-indicator.right{transform:translateX(110px)}.cmx-dock button{position:relative;background:transparent;color:#566658;font-weight:650;height:45px}.cmx-dock button[aria-pressed=true]{color:var(--gold)}.cmx-dock button span{padding-left:5px}.cmx-empty,.cmx-error{position:absolute;left:50%;transform:translateX(-50%);z-index:800;background:#fffffff2;border:1px solid white;box-shadow:0 10px 30px #14231120;border-radius:17px;padding:17px;text-align:center}.cmx-empty{top:48%;width:min(340px,90%)}.cmx-empty button{background:var(--gold);border-radius:12px;padding:10px 16px;margin-top:10px}.cmx-error{bottom:95px;font-size:12px}.cmx-error button{margin-left:12px;background:transparent}
.cmx-detail-shade{position:absolute;inset:0;background:#14261dbb;backdrop-filter:blur(9px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:24px}.cmx-detail{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(280px,.8fr);width:min(900px,100%);max-height:100%;background:#fff;border-radius:24px;overflow:hidden;box-shadow:0 20px 70px #0004}.cmx-detail-image{min-height:400px;background:#1b251e}.cmx-detail-image .cmx-media{height:100%;aspect-ratio:auto}.cmx-detail-image .cmx-media img,.cmx-detail-image .cmx-media video{object-fit:contain}.cmx-detail-info{position:relative;padding:37px 29px;overflow-y:auto}.cmx-detail h2{font-size:36px;line-height:1.07;letter-spacing:-1.5px;margin:11px 0}.cmx-detail-author{color:#69776c}.cmx-detail-author button{background:transparent;text-decoration:underline}.cmx-detail-close{position:absolute;top:10px;right:14px;background:#f1f2ea;border-radius:50%;width:36px;height:36px;font-size:26px}.cmx-detail-pages{display:flex;align-items:center;justify-content:space-between;gap:10px;margin:20px 0}.cmx-detail-pages button{font-size:28px;background:#eef0e9;border-radius:10px;width:45px;height:37px}.cmx-detail-pages span{font-size:11px;color:#637064}.cmx-detail-thumbs{display:flex;gap:7px;overflow-x:auto;padding-bottom:10px}.cmx-detail-thumbs button{flex:0 0 55px;height:62px;background:#22302a;color:var(--gold);border:2px solid transparent;border-radius:9px;overflow:hidden}.cmx-detail-thumbs button.selected{border-color:#cdbf37}.cmx-detail-thumbs img{height:100%;width:100%;object-fit:cover}.cmx-detail-buttons{display:grid;gap:10px;margin-top:20px}.cmx-detail-buttons a{padding:13px 18px;text-align:center;background:#f0f2ea;border-radius:11px}.cmx-detail-buttons a.cmx-cta{background:var(--gold);font-weight:750}.cmx-original{display:block;font-size:12px;margin-top:18px;color:#7b8674}
@media(max-width:1100px){.cmx-post-grid{grid-template-columns:repeat(3,minmax(0,1fr))}.cmx-map-rail .cmx-spot-2{display:none}}
@media(max-width:800px){.cmx-post-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.cmx-header{left:12px;right:12px;top:12px;gap:6px}.cmx-logo{padding:11px;font-size:19px}.cmx-search{height:43px;padding:0 9px}.cmx-near{padding:12px 8px;font-size:11px}.cmx-area-bar{left:12px;right:12px;top:67px}.cmx-map-rail{left:12px;bottom:95px}.cmx-spot{width:130px;height:160px}.cmx-spot-0{width:144px;height:190px}.cmx-feed{padding:125px 12px 110px}.cmx-detail{grid-template-columns:1fr;grid-template-rows:minmax(0,1fr) auto;max-height:100%;width:min(520px,100%)}.cmx-detail-image{min-height:0;max-height:50dvh}.cmx-detail-image .cmx-media{height:50dvh}.cmx-detail-info{padding:22px}.cmx-detail h2{font-size:26px}.cmx-detail-shade{padding:8px}}
@media(max-width:550px){.cmx-logo{font-size:17px}.cmx-search input{font-size:11px}.cmx-near{font-size:10px}.cmx-map-count{top:118px;left:12px}.cmx-map-rail{bottom:95px;max-width:calc(100% - 24px)}.cmx-spot{width:110px;height:135px}.cmx-spot-0{width:122px;height:157px}.cmx-map-rail .cmx-spot-2{display:none}.cmx-spot-overlay strong{font-size:10px}.cmx-feed{padding:105px 0 90px;scroll-snap-type:y mandatory}.cmx-feed-intro{padding:10px 16px 16px;margin:0}.cmx-feed-intro h1{font-size:30px}.cmx-post-grid{grid-template-columns:1fr;gap:10px}.cmx-post{border-radius:0;scroll-snap-align:start;scroll-snap-stop:always;min-height:calc(100dvh - 196px);display:flex;flex-direction:column;border-left:0;border-right:0}.cmx-post-picture{flex:1;min-height:0}.cmx-post-picture .cmx-media{height:100%;max-height:calc(100dvh - 335px);aspect-ratio:auto}.cmx-post-copy{padding:13px 18px 17px}.cmx-post h2{font-size:27px}.cmx-post-links{margin-top:10px}.cmx-post-links a.cmx-book{padding:10px 25px;font-size:13px}.cmx-detail-image .cmx-media{height:43dvh}.cmx-detail-info{max-height:48dvh}.cmx-detail-buttons{margin-top:9px}.cmx-detail h2{font-size:25px}.cmx-dock{bottom:max(12px,env(safe-area-inset-bottom))}}
@media(prefers-reduced-motion:reduce){.cmx-shell *{animation:none!important;transition:none!important}}
`;
